import FormSelect from 'components/Select/FormSelect';
import { ISelectOption } from 'components/Select/types';
import Text from 'components/Text/Text';
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { IItemForGetShortInfo, ITotalPrice } from 'types';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfo[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setModifierGroups: (value: string[]) => void;
	selectedModifierGroups: string[];
	totalPrices: ITotalPrice[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	selectedModifierGroups,
	totalPrices,
	setModifierGroups,
}) => {
	const [searchString, setSearchString] = useState<string>('');

	const filteredData = useMemo<ISelectOption[]>(() => {
		return searchString ? data.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase())) : data;
	}, [searchString, data]);

	const handleSelectAllData = useCallback(() => {
		const isCheckedAllItems = data.every((el) => selectedModifierGroups.includes(el.id));
		const dataIDs = data.map((el) => el.id);
		if (isCheckedAllItems) {
			setModifierGroups(selectedModifierGroups.filter((el) => !dataIDs.includes(el)));
		} else {
			const filteredData = data.map((el) => el.id).filter((el) => !selectedModifierGroups.includes(el));
			setModifierGroups([...selectedModifierGroups, ...filteredData]);
		}
	}, [data, selectedModifierGroups, setModifierGroups]);

	useEffect(() => {
		setSearchString('');
	}, [totalPrices]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const isChecked = () => {
				return data.every((el) => selectedModifierGroups.includes(el.id));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={!!(data.length && isChecked())}
							disabled={!data.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllModifierGroupsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[data, selectedModifierGroups, handleSelectAllData, setSearchString, searchString],
	);

	return (
		<FormSelect
			name="selectedModifierGroups"
			label="modifiersGroupLabel"
			options={filteredData}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			onChange={setModifierGroups}
			disabled={!totalPrices.length || isLoading}
			dropdownRender={renderSelectMenu}
			mode="multiple"
			showSearch={false}
			counterValue={selectedModifierGroups.length}
		/>
	);
};

export default React.memo(MultipleSelect);
